'use client';

import { useEffect, useState, useRef, useTransition } from 'react';
import { usePathname } from 'next/navigation';

interface TopProgressBarProps {
  height?: number;
  color?: string;
  showSpinner?: boolean;
  crawlSpeed?: number;
  speed?: number;
  className?: string;
  zIndex?: number;
}

export default function TopProgressBar({
  height = 3,
  color = '#f5365c',
  showSpinner = true,
  crawlSpeed = 150,
  speed = 300,
  className = '',
  zIndex = 9999,
}: TopProgressBarProps) {
  const pathname = usePathname();
  const [isPending, start] = useTransition();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Start progress bar
  const startProgress = () => {
    setVisible(true);
    setProgress(20);

    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        // Slowly increase to max 95%
        if (prev < 95) return prev + Math.random() * 5;
        return prev;
      });
    }, 200);
  };

  // Complete progress bar
  const finishProgress = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setProgress(100);
    setTimeout(() => {
      setVisible(false);
      setProgress(0);
    }, 300);
  };

  // Auto start progress on pathname change
  useEffect(() => {
    if (!pathname) return;
    startProgress();
  }, [pathname]);

  // Hide bar when navigation completes
  useEffect(() => {
    if (!isPending && visible) {
      finishProgress();
    }
  }, [isPending, visible]);

  if (!visible) return null;

  return (
    <div className="fixed left-0 top-0 z-50 h-[3px] w-full bg-transparent">
      <div
        className="h-full bg-primary transition-all duration-200 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}
